import React, { useEffect, useState } from 'react';
import {
    mainMenuTypes,
    IMenuItem,
    PasslyInfiniteScroll,
    PageLoader,
    PasslyTableRow,
    PasslyTableCell,
    PasslyTableSortLabel,
    Filters,
    PasslyFilterInput,
    PasslyFilterSelect,
    PasslyDotMenu,
    FabMenu,
    ModalComponent,
    PasslyCheckbox
} from '@app/components';
import {
    getMenuItemById,
    infiniteScrollItemsFetchLimit,
    genericErrorToast,
    TIME_DATE_FORMAT,
    passlyFormatDate,
    DATE_FORMAT,
    hasPermission,
    store,
    provisionStateSelectData,
    TIME_FORMAT,
    localStore,
    booleanToYesNo,
    hasDefaultOrgAdminPermission,
    isDefaultOrgUser,
    isLoggedInToDefaultOrg
} from '@app/helpers';
import {
    IListPageBarEnabled,
    OrderType,
    Order,
    IActiveFilter,
    ITableHead,
    FilterEnum,
    ITenants,
    IUserData,
    ITenantsLicenseEntries,
    IDotMenu,
    IFabMenu
} from '@app/models';
import { useSetPage, useTypedTranslation } from '@app/hooks';
import { useSelector, useDispatch } from 'react-redux';
import { tenantsService, basicService } from '@app/services';
import { Table, TableBody, TableHead, TableRow, TableCell, Link } from '@material-ui/core';
import { resetState, setOrderByDirection, setOrderByProperty, setActionMenuItems } from '@app/store/actions';
import { AxiosError } from 'axios';
import { ManageAccount, ManageTenant, SupportTenant } from '@app/store/constants';
import { confirm } from '@app/components/Modal/confirmModal';
import { toast } from 'react-toastify';
import { AddTenantDialog } from '@app/pages/systemManager/dialogs/addTenant.dialog';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';

interface ITenantsTableData {
    checked: boolean;
    homeRealm: string;
    created: string;
    provisioningState: string;
    licenseState: string;
    id: string;
    licenseExpiry: string;
    salesForceId: string;
    name: string;
    userCount: number;
    organizationCount: number;
    aorName: string;
    aorEmail: string;
    licenseEntries: ITenantsLicenseEntries[];
    tenantType: string;
}

enum TableHeadEnum {
    homeRealm = 'HomeRealm',
    created = 'Created',
    provisioningState = 'ProvisioningState',
    licenseState = 'License/State',
    licenseExpiry = 'License/Expiry',
    salesForceId = 'SalesForceId',
    k1Tenant = 'TenantType'
}

const TenantsPage = () => {
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Tenants);

    const [tenantCount, setTenantCount] = useState(0);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState(TableHeadEnum.homeRealm.toString());
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<ITenantsTableData[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    const [openAddTenant, setOpenAddTenant] = useState<boolean>(false);

    const [loadingData, setLoadingData] = useState<boolean>(true);
    const filtersEnabled = useSelector((state: any) => state.listPageBar.sortingFiltersEnabled);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);

    const currentUser = store.getState().signIn.currentUser as IUserData;

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: true,
        orderByEnabled: true,
        organizationsEnabled: false,
        actionMenuEnabled: true
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.homeRealm,
            label: 'HOMEREALM'
        },
        {
            id: TableHeadEnum.created,
            label: 'TENANT_CREATED'
        },
        {
            id: TableHeadEnum.provisioningState,
            label: 'TENANTS_STATE'
        },
        {
            id: TableHeadEnum.licenseState,
            label: 'TENANT_LICENSE_STATE'
        },
        {
            id: TableHeadEnum.licenseExpiry,
            label: 'TENANTS_LICENSE_EXPIRY'
        },
        {
            id: TableHeadEnum.salesForceId,
            label: 'SALESFORCE_ID'
        },
        {
            id: TableHeadEnum.k1Tenant,
            label: 'TENANT_K1'
        }
    ];

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        setLoadingData(true);
        localStore.removeItem('tenantNotificationReceivers');
    }, []);

    useEffect(() => {
        dispatch(setActionMenuItems(actionMenuItems));
    }, [rows]);

    const handleSuccess = (response: any) => {
        const count = response.data['@odata.count'];

        if (count !== undefined) {
            setTenantCount(count);
        }

        const results: ITenants[] = response.data.value;

        if (results) {
            let newRows = results.map((tenant: ITenants) => {
                const data: ITenantsTableData = {
                    checked: false,
                    id: tenant.Id,
                    homeRealm: tenant.HomeRealm,
                    created: passlyFormatDate(tenant.Created, TIME_DATE_FORMAT),
                    provisioningState: tenant.ProvisioningState,
                    licenseState: tenant.LicenseState,
                    licenseExpiry: passlyFormatDate(tenant.LicenseExpiry, DATE_FORMAT),
                    salesForceId: tenant.SalesforceId,
                    name: tenant.Name,
                    userCount: tenant.Users,
                    organizationCount: tenant.Organizations,
                    licenseEntries: tenant.LicenseEntries,
                    aorEmail: tenant.AorEmail,
                    aorName: tenant.AorName,
                    tenantType: tenant.TenantType
                };
                return data;
            });

            if (results.length === 0) {
                setRows([]);
            } else if (count !== undefined) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }

        setLoadingData(false);
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        tenantsService.getTenants(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, isReset, handleSuccess, handleError, activeFilters);
    };

    const handleError = (response: AxiosError<any>) => {
        setLoadingData(false);

        genericErrorToast(response);
    };

    const viewTenant = (row: ITenantsTableData) => {
        basicService.redirect(`/system/tenants/details/${row.id}`);
    };

    const updateAllCheckboxes = (checked: boolean) => {
        setSelectAll(checked);

        let newRows = [...rows];
        newRows.forEach(function (newRow) {
            if (newRow.id !== currentUser.tid) {
                newRow.checked = checked;
            }
        });

        setRows(newRows);
    };

    const onCheckboxChange = (index: number) => (checked: boolean) => {
        if (rows[index].id !== currentUser.tid) {
            let newRows = [...rows];
            newRows[index].checked = checked;
            setRows(newRows);
        }
    };

    const exportCsv = () => {
        const dateFormatter = function (str: string) {
            if (!str || str === '' || str === 'null') {
                return '';
            }

            return passlyFormatDate(str, DATE_FORMAT) + ' ' + passlyFormatDate(str, TIME_FORMAT);
        };

        let csv = convertArrayOfObjectsToCSV({
            data: rows,
            ignore: ['licenseEntries', 'checked'],
            format: {
                Created: dateFormatter,
                LicenseExpiry: dateFormatter
            }
        });

        let data: any;
        let filename: any;
        let link: any;

        if (csv == null) {
            return;
        }

        filename = 'tenants.csv';

        if (!csv.match(/^data:text\/csv/i)) {
            csv = 'data:text/csv;charset=utf-8,' + csv;
        }
        data = encodeURI(csv);

        link = document.createElement('a');
        link.setAttribute('href', data);
        link.setAttribute('download', filename);
        link.click();
    };

    const convertArrayOfObjectsToCSV = (args: any) => {
        let result: any;
        let ctr: any;
        let keys: any;
        let columnDelimiter: any;
        let lineDelimiter: any;
        let data: any;

        data = args.data || null;
        if (data == null || !data.length) {
            return null;
        }

        columnDelimiter = args.columnDelimiter || ',';
        lineDelimiter = args.lineDelimiter || '\n';

        keys = Object.keys(data[0]);

        if (args.ignore && args.ignore.length > 0) {
            args.ignore.forEach(function (i: number) {
                let index = keys.indexOf(i);

                if (index >= 0) {
                    keys.splice(index, 1);
                }
            });
        }

        result = '';
        result += keys.join(columnDelimiter);
        result += lineDelimiter;

        data.forEach(function (item: any) {
            ctr = 0;
            keys.forEach(function (key: any) {
                if (ctr > 0) {
                    result += columnDelimiter;
                }

                let val = item[key];

                if (args.format && args.format[key]) {
                    let formatter = args.format[key];

                    val = formatter(val);
                }

                if (!val || val === '' || val === 'null') {
                    val = '';
                }

                result += val;
                ctr++;
            });

            result += lineDelimiter;
        });

        return result;
    };

    const sendNotification = () => {
        // send selected tenants a notification
        let json: any = {};
        json.Tenants = [];

        let i: number = 0;
        rows.forEach(function (tenant: ITenantsTableData) {
            if (tenant.checked) {
                json.Tenants[i] = {};
                json.Tenants[i].TenantId = tenant.id;
                json.Tenants[i].HomeRealm = tenant.homeRealm;

                i++;
            }
        });

        localStore.setItem('tenantNotificationReceivers', JSON.stringify(json));

        basicService.redirect('/system/notifications');
    };

    const deleteRow = (e: any) => {
        const chosenId: string = e.currentTarget.id;

        confirm(translation('DELETE_TENANT_BODY'), translation('DELETE'), translation('CANCEL'), translation('DELETE_TENANT_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                let json: any = {};
                json.Tenants = [];
                json.Tenants[0] = chosenId;

                tenantsService.deleteTenant(json, handleError, handleSuccessDelete);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const handleSuccessDelete = (response: any) => {
        setLoadingData(true);
        fetchMoreData(0, true);
    };

    const sendOnboardEmail = (e: any) => {
        const chosenId: string = e.currentTarget.id;

        confirm(translation('RESET_AOR_WARNING'), translation('UPDATE_BUTTON'), translation('CANCEL'), translation('RESET_AOR_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                tenantsService.resetAorToken(chosenId, handleError, handleSuccessResetAorToken);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const forceSignoutDialog = (e: any) => {
        const tenantId: string = e.currentTarget.id;

        confirm(translation('FORCE_SIGNOUT_TENANT_CONFIRM'), translation('SIGNOUT'), translation('NO'), translation('CONFIRM'), false, {
            isDeleteAction: true
        }).then(
            () => {
                tenantsService.forceSignout(tenantId)
                    .then(() => {
                        dispatch(handleSetSnackbar({ show: true, message: translation('TENANT_FORCE_SIGNED_OUT'), severity: 'success' }));
                    })
                    .catch(() => {
                        dispatch(handleSetSnackbar({ show: true, message: translation('GENERIC_ERROR'), severity: 'error' }))
                    });
            },
            () => {
                // on cancel
            }
        );
    };

    const handleSuccessResetAorToken = () => {
        toast(translation('ONBOARDING_EMAIL_SENT'));
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('EXPORT_TENANT_LIST'),
            action: exportCsv
        },
        {
            name: translation('SEND_NOTIFICATION'),
            action: sendNotification
        }
    ];

    const rowMenuItems: IDotMenu[] = [
        {
            name: translation('ONBOARD_BUTTON'),
            action: sendOnboardEmail,
            permitted: hasPermission([ManageTenant]) || hasPermission([SupportTenant])
        },
        {
            name: translation('SIGNOUT'),
            action: forceSignoutDialog,
            permitted: hasDefaultOrgAdminPermission() && isDefaultOrgUser() && isLoggedInToDefaultOrg()
        },
        {
            name: translation('DELETE'),
            action: deleteRow,
            permitted: hasPermission([ManageTenant])
        }
    ];

    const addNewTenant = () => {
        setOpenAddTenant(true);
    };

    const handleModalCloseCallback = () => {
        setOpenAddTenant(!openAddTenant);
    };

    const addMenu: IFabMenu = {
        name: 'NEW_TENANT',
        actions: [
            {
                name: 'NEW_TENANT',
                method: () => addNewTenant(),
                iconElement: <i className='icon icon-provisioning' />
            }
        ]
    };

    return (
        <>
            <ModalComponent
                headerTitle={translation('NEW_TENANT')}
                modalSwitcherCallback={handleModalCloseCallback}
                form={<AddTenantDialog dialogOpen={handleModalCloseCallback} />}
                localModalSwitcher={openAddTenant}
            />

            {filtersEnabled && (
                <Filters>
                    <PasslyFilterInput label={'HOMEREALM'} filtername={FilterEnum.contains} filterby={TableHeadEnum.homeRealm} autoFocus={true} />
                    <PasslyFilterSelect data={provisionStateSelectData} label={'TENANTS_STATE'} filtername={FilterEnum.provisioningState} />
                </Filters>
            )}

            {!loadingData && hasPermission([ManageTenant]) && <FabMenu menu={addMenu} hideTooltip={true} />}

            <PasslyInfiniteScroll totalCount={tenantCount} currentCount={rows.length} onFetch={fetchMoreData}>
                <div className='user__table notification'>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {hasPermission([ManageAccount]) ? (
                                    <TableCell style={{ width: 45 }}>
                                        <PasslyCheckbox name='selectall' checked={selectAll} onChange={updateAllCheckboxes} label='' />
                                    </TableCell>
                                ) : (
                                    <TableCell style={{ width: 70 }}>&nbsp;</TableCell>
                                )}
                                {headCells.map((headCell: ITableHead, index: number) => (
                                    <TableCell key={index}>
                                        <PasslyTableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={order}
                                            onClick={createSortHandler(headCell.id)}
                                        >
                                            {translation(headCell.label)}
                                        </PasslyTableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row: ITenantsTableData, index: number) => (
                                <PasslyTableRow key={index}>
                                    {hasPermission([ManageAccount]) && row.id !== currentUser.tid ? (
                                        <PasslyTableCell align='center'>
                                            <PasslyCheckbox name='select' checked={row.checked} onChange={onCheckboxChange(index)} label='' />
                                        </PasslyTableCell>
                                    ) : (
                                        <PasslyTableCell align='center'>&nbsp;</PasslyTableCell>
                                    )}
                                    <PasslyTableCell>
                                        <Link component='button' onClick={() => viewTenant(row)}>
                                            {row.homeRealm}
                                        </Link>
                                        <div style={{ fontSize: 11 }}>
                                            {row.name}
                                            <br />
                                            {translation('USER_COUNT')}: {row.userCount}&nbsp; {translation('ORG_COUNT')}: {row.organizationCount}
                                        </div>
                                    </PasslyTableCell>
                                    <PasslyTableCell>{row.created}</PasslyTableCell>
                                    <PasslyTableCell>{row.provisioningState}</PasslyTableCell>
                                    <PasslyTableCell>
                                        {row.licenseState}
                                        {row.licenseEntries.map((entry, index) => (
                                            <div style={{ fontSize: 11 }} key={index}>
                                                <span>{entry.Name}: </span>
                                                <span>
                                                    {entry.Active}/{entry.Seats}
                                                </span>
                                            </div>
                                        ))}
                                    </PasslyTableCell>
                                    <PasslyTableCell>{row.licenseExpiry}</PasslyTableCell>
                                    <PasslyTableCell>{row.salesForceId}</PasslyTableCell>
                                    <PasslyTableCell>{translation(booleanToYesNo(row.tenantType === 'K1'))}</PasslyTableCell>
                                    {(hasPermission([ManageTenant]) || hasPermission([SupportTenant])) && (
                                        <PasslyTableCell>
                                            <PasslyDotMenu id={row.id} options={rowMenuItems} />
                                        </PasslyTableCell>
                                    )}
                                </PasslyTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </PasslyInfiniteScroll>

            {loadingData && <PageLoader />}

            {!loadingData && rows.length === 0 && <div className='table--empty'>{translation('EMPTY_TENANT_LIST')}</div>}
        </>
    );
};

export default TenantsPage;
